import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  Grid, 
  Divider, 
  Tabs, 
  Tab, 
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FilterListIcon from '@mui/icons-material/FilterList';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { db } from '../../../firebase';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { useOrganization } from '../../../contexts/OrganizationContext';
import DashboardMetricCard from './components/DashboardMetricCard';
import ContactList from './components/ContactList';
import ImportDialog from './components/ImportDialog';
import AIImportDialog from './components/AIImportDialog';
import EventsTab from './components/EventsTab';
import ContactAnalyticsTab from './components/ContactAnalyticsTab';
import UserMetricsTab from './components/UserMetricsTab';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contacts-tabpanel-${index}`}
      aria-labelledby={`contacts-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `contacts-tab-${index}`,
    'aria-controls': `contacts-tabpanel-${index}`,
  };
}

// Event Attendance interface
interface EventAttendance {
  eventId?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  ticketType?: string;
  status?: string;
  attendeeId?: string;
  importDate: string;
  notes?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
  engagementScore: number;
  lastContactDate?: Date;
  source: string;
  createdAt: Date;
  consentStatus: 'granted' | 'denied' | 'unknown';
  // For backward compatibility
  eventName?: string;
  eventDate?: string;
  eventDescription?: string;
  importDate?: string;
  // New field for multiple event attendance
  eventAttendance?: EventAttendance[];
}

const OrganizationContacts: React.FC = () => {
  // Update parameter name to match route in App.tsx
  const { id } = useParams<{ id: string }>();
  const { activeOrganization } = useOrganization();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState<boolean>(true);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [aiImportDialogOpen, setAIImportDialogOpen] = useState<boolean>(false);
  const [metrics, setMetrics] = useState({
    totalContacts: 0,
    newContacts: 0,
    activeContacts: 0,
    engagementRate: 0
  });
  const [error, setError] = useState<string | null>(null);

  // Check URL for refresh parameter and trigger data refresh
  useEffect(() => {
    // Always fetch contacts on initial component mount
    if (id) {
      fetchContacts();
    }
    
    // Listen for refresh events
    const handlePopState = () => {
       const params = new URLSearchParams(window.location.search);
       if (params.has('refresh')) {
          // Clean up URL without page reload
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);

          // Refresh contacts data
          if (id) fetchContacts();
       }
    };

    // Add event listener for both popstate and locationchange
    window.addEventListener('popstate', handlePopState);

    // Create a custom event listener for any component to trigger a refresh
    window.addEventListener('contactsUpdated', () => {
       if (id) fetchContacts();
    });
    
    // Check URL on mount
    handlePopState();
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('contactsUpdated', () => {
        if (id) fetchContacts();
      });
    };
  }, [id]); // Only re-run when ID changes
  
  // Fetch contacts
  const fetchContacts = async () => {
    if (!id) {
      console.error("No organization ID found");
      setError("Organization ID is missing. Please try again or contact support.");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {

      const contactsRef = collection(db, `organizations/${id}/contacts`);
      const contactsSnapshot = await getDocs(contactsRef);

      const contactsData: Contact[] = [];

      // Process each document safely
      contactsSnapshot.docs.forEach((doc) => {
         try {
            const data = doc.data();
            contactsData.push({
               id: doc.id,
               firstName: data.firstName || '',
               lastName: data.lastName || '',
               email: data.email || '',
               phone: data.phone || '',
               company: data.company || '',
               title: data.title || '',
               tags: Array.isArray(data.tags) ? data.tags : [],
               engagementScore: typeof data.engagementScore === 'number' ? data.engagementScore : 0,
               lastContactDate: data.lastContactDate
                  ? new Date(data.lastContactDate.seconds * 1000)
                  : undefined,
               source: data.source || 'manual',
               createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
               consentStatus: data.consentStatus || 'unknown',
               eventName: data.eventName || '',
               eventDate: data.eventDate || '',
               eventDescription: data.eventDescription || '',
               importDate: data.importDate || '',
               eventAttendance: data.eventAttendance || [],
            });
         } catch (docError) {
            console.error('Error processing contact document:', doc.id, docError);
         }
      });
      

      
      // Calculate metrics
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const newContactsCount = contactsData.filter(contact => contact.createdAt > thirtyDaysAgo).length;
      const activeContactsCount = contactsData.filter(contact => 
        contact.lastContactDate && contact.lastContactDate > thirtyDaysAgo
      ).length;
      
      const totalContacts = contactsData.length;
      const engagementRate = totalContacts > 0 ? Math.round((activeContactsCount / totalContacts) * 100) : 0;
      
      setMetrics({
        totalContacts,
        newContacts: newContactsCount,
        activeContacts: activeContactsCount,
        engagementRate
      });
      
      setContacts(contactsData);
      setFilteredContacts(contactsData);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Failed to load contacts. Please refresh the page or try again later.');
    } finally {
      // Always set loading to false, whether successful or not
      setLoading(false);
    }
  };

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredContacts(contacts);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = contacts.filter(contact => 
      contact.firstName.toLowerCase().includes(query) ||
      contact.lastName.toLowerCase().includes(query) ||
      contact.email.toLowerCase().includes(query) ||
      (contact.phone && contact.phone.includes(query)) ||
      contact.tags.some(tag => tag.toLowerCase().includes(query))
    );
    
    setFilteredContacts(filtered);
  }, [searchQuery, contacts]);

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle import dialog
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
  };

  // Handle AI import dialog
  const handleOpenAIImportDialog = () => {
    setAIImportDialogOpen(true);
  };

  const handleCloseAIImportDialog = () => {
    setAIImportDialogOpen(false);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Contact Management
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your organization's contacts and leverage your network
        </Typography>
      </Box>

      {/* Search and Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search contacts by name, email, phone or tags..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSearchQuery('')}
                      edge="end"
                      size="small"
                    >
                      ×
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button 
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => {}}
              >
                Filters
              </Button>
              <Button 
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={handleOpenImportDialog}
              >
                Import
              </Button>
              <Button 
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleOpenAIImportDialog}
              >
                AI Import
              </Button>
              <Button 
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/organizations/${id}/contacts/create?refresh=true`)}
              >
                Add Contact
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Dashboard Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Total Contacts"
            value={metrics.totalContacts}
            icon="people"
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="New Contacts (30d)"
            value={metrics.newContacts}
            icon="new_releases"
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Active Contacts"
            value={metrics.activeContacts}
            icon="check_circle"
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Engagement Rate"
            value={`${metrics.engagementRate}%`}
            icon="trending_up"
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Tabs and Content */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="contact management tabs"
          >
            <Tab label="All Contacts" {...a11yProps(0)} />
            <Tab label="Recently Added" {...a11yProps(1)} />
            <Tab label="High Engagement" {...a11yProps(2)} />
            <Tab label="Needs Attention" {...a11yProps(3)} />
            <Tab label="Events" {...a11yProps(4)} />
            <Tab label="Analytics" {...a11yProps(5)} icon={<AnalyticsIcon />} />
            <Tab label="User Metrics" {...a11yProps(6)} icon={<AssessmentIcon />} />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <ContactList 
            contacts={filteredContacts} 
            loading={loading} 
            organizationId={id || ''} 
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <ContactList 
            contacts={filteredContacts.filter(c => {
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              return c.createdAt >= thirtyDaysAgo;
            })} 
            loading={loading} 
            organizationId={id || ''} 
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <ContactList 
            contacts={filteredContacts.filter(c => c.engagementScore >= 70)} 
            loading={loading} 
            organizationId={id || ''} 
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <ContactList 
            contacts={filteredContacts.filter(c => c.engagementScore < 30)} 
            loading={loading} 
            organizationId={id || ''} 
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <EventsTab organizationId={id || ''} />
        </TabPanel>
        
        <TabPanel value={tabValue} index={5}>
          <ContactAnalyticsTab organizationId={id || ''} />
        </TabPanel>
        
        <TabPanel value={tabValue} index={6}>
          <UserMetricsTab organizationId={id || ''} />
        </TabPanel>
      </Paper>

      {/* Import Dialog */}
      <ImportDialog 
        open={importDialogOpen} 
        onClose={handleCloseImportDialog}
        organizationId={id || ''}
      />

      {/* AI Import Dialog */}
      <AIImportDialog
        open={aiImportDialogOpen}
        onClose={handleCloseAIImportDialog}
        organizationId={id || ''}
      />
      
      {error && (
        <Box sx={{ mt: 3, p: 2, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="body1" color="error.main">
            {error}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default OrganizationContacts;
